import { useMemo, useState } from 'react';
import { PencilSquareIcon, TrashIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { PageHeader, Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import SportBadge from '@/components/ui/SportBadge';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Dropdown from '@/components/ui/Dropdown';
import EmptyState from '@/components/ui/EmptyState';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { BulkDeleteBar, SelectAllHeader, SelectRowCell } from '@/components/ui/BulkDelete';
import { useRowSelection } from '@/lib/useRowSelection';
import { useDebounce } from '@/lib/useDebounce';
import { useSports } from '@/features/reference/useReference';
import { deleteZone as deleteZoneById, type Zone, type ZonePayload } from '@/api/zonesService';
import { useZones } from './useZones';
import ZoneModal from './ZoneModal';

const rowId = (z: Zone) => z.id;

const ZonesPage = () => {
  const [search, setSearch] = useState('');
  const [sport, setSport] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const filters = useMemo(
    () => ({ search: debouncedSearch || undefined, sport: sport || undefined }),
    [debouncedSearch, sport]
  );

  const { data: sports = [] } = useSports();
  const { zones, isLoading, createZone, updateZone, deleteZone, isSaving, invalidate } = useZones(filters);
  const selection = useRowSelection(zones, rowId, filters);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Zone | null>(null);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };
  const openEdit = (zone: Zone) => {
    setEditing(zone);
    setModalOpen(true);
  };

  const handleSubmit = (payload: ZonePayload) =>
    editing ? updateZone({ id: editing.id, payload }) : createZone(payload);

  const handleDelete = (zone: Zone) => {
    if (zone.inUse) return;
    if (window.confirm(`Supprimer la zone « ${zone.label} » ?`)) deleteZone(zone.id);
  };

  return (
    <>
      <PageHeader
        title="Zones d'intensité"
        description="Entrainement / Zones"
        action={<Button onClick={openCreate}>+ Ajouter</Button>}
      />

      <Card className="mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-64">
            <Input
              label="Rechercher"
              placeholder="Libellé ou identifiant"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="w-48">
            <Select label="Sport" value={sport} onChange={(e) => setSport(e.target.value)}>
              <option value="">Tous</option>
              {sports.map((s) => (
                <option key={s.identifier} value={s.identifier}>
                  {s.label}
                </option>
              ))}
            </Select>
          </div>
        </div>
      </Card>

      {isLoading && <p className="text-sm text-gray-500">Chargement…</p>}

      {!isLoading && zones.length === 0 && (
        <EmptyState
          icon={<AdjustmentsHorizontalIcon />}
          title="Aucune zone"
          description="Ajoutez des zones d'intensité pour alimenter le décodeur et les intervalles."
        />
      )}

      {!isLoading && zones.length > 0 && (
        <Card className="p-0">
          <BulkDeleteBar
            selection={selection}
            labelOf={(z) => z.label}
            remove={(z) => deleteZoneById(z.id)}
            onDone={invalidate}
          />
          <Table>
            <THead>
              <tr>
                <SelectAllHeader selection={selection} />
                <TH>Libellé</TH>
                <TH>Sport</TH>
                <TH>%IC</TH>
                <TH className="text-right">Actions</TH>
              </tr>
            </THead>
            <TBody>
              {zones.map((zone) => (
                <TR key={zone.id}>
                  <SelectRowCell selection={selection} row={zone} />
                  <TD className="font-bold text-gray-900 dark:text-gray-100" title={zone.identifier}>
                    {zone.label}
                  </TD>
                  <TD>
                    <SportBadge identifier={zone.sportIdentifier} label={zone.sportLabel} />
                  </TD>
                  <TD>
                    {zone.pctIcMin}–{zone.pctIcMax}
                  </TD>
                  <TD className="text-right">
                    <Dropdown
                      actions={[
                        {
                          label: 'Modifier',
                          icon: <PencilSquareIcon className="h-4 w-4" />,
                          onClick: () => openEdit(zone),
                        },
                        {
                          label: zone.inUse ? 'Utilisée — suppression bloquée' : 'Supprimer',
                          icon: <TrashIcon className="h-4 w-4" />,
                          destructive: true,
                          onClick: () => handleDelete(zone),
                        },
                      ]}
                    />
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}

      <ZoneModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        zone={editing}
        sports={sports}
        isSaving={isSaving}
      />
    </>
  );
};

export default ZonesPage;
